<script lang="ts" setup>
const { cart, shippingCosts, selectedShippingMethod, removeCouponFromCart } = useCart()

export interface Props {
  showCurrency?: boolean
}

// @TODO: STRAPI TOOLTIPS
const { loadLayout, tooltips } = useLayout()
await loadLayout()

defineProps<Props>()

const totalPrice = computed(() => {
  return {
    regularPrice: cart.value?.prices?.grand_total?.value ?? 0,
  }
})

const estimatedShippingCosts = computed<{ value: number, currency: string }>(() => {
  if (cart.value?.prices?.grand_total?.value < 0.01) {
    return {
      value: 0.00,
      currency: 'EUR',
    }
  }
  if (selectedShippingMethod.value) {
    return shippingCosts.value
  }
  if (cart.value.estimate_shipping_cost?.value > 0) {
    return cart.value.estimate_shipping_cost
  }
  return {
    value: 0.00,
    currency: 'EUR',
  }
})

const shippingCostsModal = useUiDialog('shipping-costs-modal')
const shippingCostsTooltip = computed<any | null>(() => {
  return tooltips.value.get('shipping-costs') || null
})
</script>

<template>
  <div
    v-if="cart"
    class="cart__totals"
  >
    <table>
      <tbody>
        <tr class="cart__totals-subtotal">
          <th>{{ $t('Subtotal') }}</th>
          <td>{{ $tn(cart.prices.subtotal_including_tax.value, { style: 'currency', currency: 'EUR', useGrouping: false }) }}</td>
        </tr>
        <tr
          v-for="(discount, index) in cart.prices.discounts"
          :key="`row1-${index}`"
          class="cart__totals-coupon"
        >
          <th>
            {{ $t('Discount {discountCode}', { discountCode: discount?.label }) }}
            <button
              type="button"
              class="cart__totals-shipment--info"
              @click="removeCouponFromCart"
            >
              {{ $t('verwijder') }}
            </button>
          </th>
          <td :class="{ price__rounded: discount.amount.value === Math.round(discount.amount.value) }">
            - <i18n-n
              tag="span"
              :value="discount.amount.value"
              :format="{ style: 'currency', currency: 'EUR', useGrouping: false }"
              class="price__discount-price"
            >
              <template #currency="slotProps">
                <span
                  v-if="showCurrency"
                  class="price__currency"
                >
                  {{ slotProps.currency }}
                </span>
              </template>
              <template #integer="slotProps">
                <span class="price__integer">{{ slotProps.integer }}</span>
              </template>
              <template #decimal="slotProps">
                <span class="price__decimal">{{ slotProps.decimal }}</span>
              </template>
              <template #fraction="slotProps">
                <span class="price__fraction">{{ slotProps.fraction }}</span>
              </template>
            </i18n-n>
          </td>
        </tr>
        <tr class="cart__totals-shipment">
          <th>
            <!-- {{ shippingCostsTooltip.Title }}  -->
            {{ $t('Shipping Costs') }}

            <span v-if="selectedShippingMethod">
              ({{ selectedShippingMethod.method_title }})
            </span>

            <template v-if="shippingCostsTooltip">
              <button
                type="button"
                class="cart__totals-shipment--info"
                @click="shippingCostsModal.open() "
              >
                <NuxtIcon name="icon:info-filled" />
              </button>

              <LazyUiDialog
                class="shipping-costs__modal"
                name="shipping-costs-modal"
                dialog-type="responsive"
              >
                <template #dialogHeader>
                  <span class="dialog__title">{{ shippingCostsTooltip.Title }}</span>
                </template>
                <div>
                  <StrapiBlocksText :nodes="shippingCostsTooltip.Richtext" />
                </div>
              </LazyUiDialog>
            </template>
          </th>
          <td v-if="!selectedShippingMethod">
            <slot name="shippingCosts">
              {{ $t('Calculated in checkout') }}
            </slot>
          </td>
          <td
            v-else-if="estimatedShippingCosts && estimatedShippingCosts.value > 0"
            :class="{ price__rounded: estimatedShippingCosts.value === Math.round(estimatedShippingCosts.value) }"
          >
            <i18n-n
              tag="span"
              :value="estimatedShippingCosts.value"
              :format="{ style: 'currency', currency: 'EUR', useGrouping: false }"
              class="price__shipping-price"
            >
              <template #currency="slotProps">
                <span
                  v-if="showCurrency"
                  class="price__currency"
                >
                  {{ slotProps.currency }}
                </span>
              </template>
              <template #integer="slotProps">
                <span class="price__integer">{{ slotProps.integer }}</span>
              </template>
              <template #decimal="slotProps">
                <span class="price__decimal">{{ slotProps.decimal }}</span>
              </template>
              <template #fraction="slotProps">
                <span class="price__fraction">{{ slotProps.fraction }}</span>
              </template>
            </i18n-n>
          </td>
          <td
            v-else
            class="free"
          >
            {{ $t('Free') }}
          </td>
        </tr>
        <tr class="cart__totals-grand-total">
          <th>{{ $t('Grand total') }}</th>
          <td>
            <UiPrice
              :show-currency="showCurrency"
              class="cart__totals-grand-total-price"
              :prices="totalPrice"
              :force-full-price="true"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style>
@import url("~layers/components/Cart/Totals.css");
</style>
